import { useCallback, useEffect, useRef, useState } from 'react';

import { ApiError } from '../widget-api/http-client';

function getErrorMessage(err: unknown): string {
  if (err instanceof ApiError) {
    if (err.status >= 500) return 'Сервис временно недоступен. Попробуйте ещё раз.';
    if (err.status === 401) return 'Сессия истекла. Войдите заново.';
    if (err.status === 403) return 'Нет доступа к этим данным.';
    if (err.status === 404) return 'Данные не найдены.';
    return err.detail || 'Не удалось загрузить данные. Попробуйте ещё раз.';
  }
  if (err instanceof Error) return err.message;
  return 'Не удалось загрузить данные. Попробуйте ещё раз.';
}

type AsyncState<T> = {
  data: T | null;
  loading: boolean;
  error: string | null;
};

export function useAsync<T>(
  fn: () => Promise<T>,
  deps: React.DependencyList
): AsyncState<T> & { reload: () => void; setData: (data: T) => void } {
  const [state, setState] = useState<AsyncState<T>>({ data: null, loading: true, error: null });
  const reloadCounterRef = useRef(0);
  const [reloadTick, setReloadTick] = useState(0);
  // Держим актуальную fn без добавления её в deps эффекта — иначе каждый рендер тригерит перезагрузку
  const fnRef = useRef(fn);
  fnRef.current = fn;

  const reload = useCallback(() => {
    reloadCounterRef.current += 1;
    setReloadTick(reloadCounterRef.current);
  }, []);

  /** Записать свежие данные сразу — до/без повторного запроса (например, me после выбора студии). */
  const setData = useCallback((data: T) => {
    setState({ data, loading: false, error: null });
  }, []);

  useEffect(() => {
    let cancelled = false;
    setState((prev) => ({ ...prev, loading: true, error: null }));

    fnRef.current()
      .then((data) => {
        if (!cancelled) setState({ data, loading: false, error: null });
      })
      .catch((err: unknown) => {
        if (!cancelled) setState({ data: null, loading: false, error: getErrorMessage(err) });
      });

    return () => {
      cancelled = true;
    };
    // deps передаются явно снаружи — fnRef.current не нужен в списке
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [...deps, reloadTick]);

  return { ...state, reload, setData };
}
